<script setup lang="ts">
import { OnClickOutside } from '@vueuse/components';
import { computed, ref } from 'vue';

import GradientButton from '@/components/GradientButton.vue';
import LoginForm from '@/components/LoginForm.vue';
import { ROUTE_NAMES, router } from '@/router';
import { useAuthenticationStore } from '@/store/useAuthenticationStore';
import { useCompetitionStore } from '@/store/useCompetitionStore';

const store = useAuthenticationStore();
const competitionStore = useCompetitionStore();

const BUTTON_STATES = {
  CANCEL: 'Cancel',
  ADMIN: 'Admin',
  LOGOUT: 'Log Out',
};

const isOpen = ref(false);
const isSignedIn = computed(() => store.isAuthorised);
const signInButtonText = computed(() =>
  isOpen.value ? BUTTON_STATES.CANCEL : BUTTON_STATES.ADMIN,
);
const signOutButtonText = computed(() =>
  isSignedIn.value && router.currentRoute.value.path.includes(ROUTE_NAMES.ADMIN)
    ? BUTTON_STATES.LOGOUT
    : BUTTON_STATES.ADMIN,
);

const onAuthorised = () => {
  isOpen.value = !isOpen.value;
  router.push({ name: ROUTE_NAMES.ADMIN });
};

const onAdminClick = () => {
  if (signOutButtonText.value === BUTTON_STATES.LOGOUT) {
    store.signOut();
    return router.push({ name: ROUTE_NAMES.HOME });
  }

  return router.push({ name: ROUTE_NAMES.ADMIN });
};

const onHomeClick = () => {
  competitionStore.initalise();
};
</script>

<template>
  <header>
    <GradientButton v-if="isSignedIn" @click="onAdminClick">
      {{ signOutButtonText }}
    </GradientButton>
    <GradientButton
      id="admin-btn"
      v-else
      @click="isOpen = !isOpen"
      data-qa="sign-in-btn"
      class="bp-m"
    >
      {{ signInButtonText }}
    </GradientButton>
    <h1>
      <router-link to="/" @click="onHomeClick">Club League Scores</router-link>
    </h1>
  </header>
  <OnClickOutside @trigger="isOpen = false" :options="{ ignore: ['#admin-btn'] }">
    <Transition name="slide-fade">
      <LoginForm v-if="isOpen" @onAuthorised="onAuthorised" />
    </Transition>
  </OnClickOutside>
</template>

<style lang="scss" scoped>
header {
  align-items: center;
  background-color: #d9d9d9;
  background: linear-gradient(180deg, white, #d9d9d9);
  border-bottom: 1px solid #afafaf;
  box-shadow: 3px 0 5px 1px #b7b7b7;
  display: flex;
  font-family: 'Open Sans Condensed', sans-serif;
  justify-content: center;
  padding: 0.25rem;
  position: relative;
  text-align: center;
  z-index: 20;
}

h1 {
  font-size: 2.25em;
  margin: 0;
}

a {
  color: #adadad;
  font-weight: 700;
  text-decoration: none;
  text-shadow:
    0 -1px 0 #535353,
    0 1px 0 white;
}

button {
  left: 1rem;
  position: absolute;
}

@media screen and (max-width: 500px) {
  .bp-m {
    display: none !important;
  }
}

.slide-fade {
  &-enter-active {
    transition: all 0.3s ease-out;
  }

  &-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
  }

  &-enter-from,
  &-leave-to {
    transform: translateY(-100%);
    opacity: 0;
  }
}
</style>
